import React, {useState} from 'react';
import {Modal, Form, Select, Input, Upload, message} from 'antd';
import {InboxOutlined} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import type {RootState} from '../../store/rootReducer';
import feedbackService from "../../services/feedbackService";
import {downscaleToJpegBase64} from "../../utils/helpers";

const {TextArea} = Input;

type FeedbackPayload = {
    type: 'bug' | 'feature' | 'general' | 'question';
    message: string;
    email?: string;
    screenshot?: string;
};

type Props = {
    open?: boolean;
    onClose?: () => void;
};

const FeedbackPage: React.FC<Props> = ({open: controlledOpen, onClose}) => {
    const user = useSelector((s: RootState) => s.auth?.user || null);
    const isAuth = !!user;

    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;

    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm<FeedbackPayload>();

    const [fileName, setFileName] = useState('');

    const MAX_B64_BYTES = 600_000;

    const beforeUpload = async (file: File) => {
        if (file.size > 5 * 1024 * 1024) {
            message.error('Please attach an image under 5MB.');
            return Upload.LIST_IGNORE;
        }

        const b64 = await downscaleToJpegBase64(file, {maxW: 1200, maxH: 1200, quality: 0.72});

        const approxBytes = Math.ceil((b64.length * 3) / 4);
        if (approxBytes > MAX_B64_BYTES) {
            message.error('Screenshot too large after compression. Please crop or use a smaller image.');
            return Upload.LIST_IGNORE;
        }

        form.setFieldsValue({screenshot: b64});
        message.success('Screenshot attached');
        return false;
    };

    const handleClose = () => {
        if (onClose) {
            setFileName('');
            onClose();
        }
        else setInternalOpen(false);
    };

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            await feedbackService.sendFeedback(values);
            message.success('Thanks! Your feedback was sent.');
            form.resetFields();
            handleClose();
        } catch (e: any) {
            if (e?.errorFields) return;
            message.error(e?.response?.data?.message || 'Could not send feedback.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                onCancel={handleClose}
                onOk={onSubmit}
                okText="Send feedback"
                confirmLoading={submitting}
                title="We’d love your feedback"
                destroyOnClose
            >
                <Form form={form} layout="vertical" initialValues={{type: 'general'}}>
                    <Form.Item
                        name="type"
                        label="Feedback type"
                        rules={[{required: true, message: 'Please select a type'}]}
                    >
                        <Select
                            options={[
                                {value: 'bug', label: 'Bug'},
                                {value: 'feature', label: 'Feature request'},
                                {value: 'general', label: 'General feedback'},
                                {value: 'question', label: 'Question'},
                            ]}
                        />
                    </Form.Item>

                    {!isAuth && (
                        <Form.Item
                            name="email"
                            label="Your email"
                            rules={[
                                {required: true, message: 'Email is required'},
                                {type: 'email', message: 'Enter a valid email'},
                            ]}
                        >
                            <Input placeholder="name@example.com"/>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="message"
                        label="Message"
                        rules={[{required: true, message: 'Please write a few words'}]}
                    >
                        <TextArea rows={5} placeholder="Tell us about a bug, idea, or question…"/>
                    </Form.Item>

                    <Form.Item name="screenshot" hidden>
                        <Input type="hidden"/>
                    </Form.Item>

                    <Form.Item label="Attach a screenshot (optional)">
                        <Upload.Dragger
                            accept="image/*"
                            multiple={false}
                            beforeUpload={beforeUpload}
                            showUploadList={false}
                            onChange={(info) => {
                                if (info.file) setFileName(info.file.name);
                            }}
                        >
                            <p className="ant-upload-drag-icon"><InboxOutlined/></p>
                            <p className="ant-upload-text">Click or drag an image here</p>
                            <p className="ant-upload-hint">
                                {fileName !== '' ? <b>Selected: {fileName}</b> : 'Image will be attached to your feedback'}
                            </p>
                        </Upload.Dragger>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default FeedbackPage;